import { Badge } from '@/app/components/ui/badge'
import { BadgeListPopover } from '@/app/components/ui/BadgeListPopover'
import { Product } from '@/app/services/pos.service'
import { Tag, PackageOpen, ImageOff } from 'lucide-react'
import { useSessionStore } from '@/app/store/auth/auth.store'

export interface ProductCardProps {
  product: Product
  addToCart: (product: Product) => void
}

export function ProductCard({ product, addToCart }: ProductCardProps) {
  const activeBranchId = useSessionStore(state => state.activeBranchId)

  const stockItems = [
    { id: 'total', name: `${product.stock || 0} u.` },
    ...(product.stocks || [])
      .filter((s) => s.sucursal_id !== activeBranchId)
      .map((s) => ({
        id: String(s.sucursal_id),
        name: `${s.sucursal_name}: ${s.stock} u.`
      }))
  ]

  const activeTiers = (product.priceTiers || []).filter(t => t.isActive && (t.lifecycleStatus === 'ACTIVE' || t.lifecycleStatus === undefined));

  const tierItems = activeTiers.map(tier => {
    let dateSuffix = '';
    if (tier.endsAt) {
      const endDate = new Date(tier.endsAt);
      // Format to simple date (e.g., 21/7/2026)
      dateSuffix = ` (hasta ${endDate.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })})`;
    }
    return {
      id: `tier-${tier.id}`,
      name: `${tier.minQuantity}+ pzas: ${Number(tier.discountPercentage)}% dto.${dateSuffix}`,
      shortName: `${tier.minQuantity}+ pzas: ${Number(tier.discountPercentage)}% dto.`,
      badgeClassName: 'bg-orange-100 hover:bg-orange-200 text-orange-700 border-transparent font-semibold'
    };
  });

  const legacyPromoItems = product.promotion
    ? [
      { id: 'promo', name: String(product.promotion), badgeClassName: 'bg-purple-100 hover:bg-purple-200 text-purple-700 border-transparent font-semibold' },
      ...(product.promotionExpiry ? [{ id: 'promo-exp', name: `Vigencia: ${new Date(product.promotionExpiry).toLocaleDateString('es-MX')}` }] : [])
    ]
    : [];

  const promoItems = [...legacyPromoItems, ...tierItems];
  const hasPromotions = promoItems.length > 0;

  return (
    <div
      onClick={() => addToCart(product)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          addToCart(product)
        }
      }}
      tabIndex={0}
      className="flex flex-col justify-between rounded-lg border border-slate-200 bg-white p-4 text-left transition-all hover:border-green-500 hover:shadow-md focus:border-green-500 focus:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 cursor-pointer h-full min-h-[380px]"
    >
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Product Image on the top left */}
        <div className="mb-4 flex justify-start">
          <div className="flex items-center justify-center w-32 h-32 rounded-lg overflow-hidden bg-primary/10">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <ImageOff className="h-10 w-10 text-primary/50" />
            )}
          </div>
        </div>

        {/* Product Details */}
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="flex-1 overflow-hidden">
            <h3 className="text-sm font-semibold text-slate-900 line-clamp-2" title={product.name}>
              {product.name}
            </h3>
            <p className="mt-1.5 text-xs font-mono text-slate-500 truncate" title={product.code}>
              {product.code}
            </p>
          </div>
          {product.requiresPrescription && (
            <span className="shrink-0 rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700 uppercase">
              Rx
            </span>
          )}
        </div>

        <p className="mb-1.5 text-xs text-slate-600 truncate">
          {product.category || 'Sin categoría'}
        </p>
        <p className="mb-4 text-xs text-slate-500 truncate">
          {product.department || 'Sin departamento'}
        </p>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="mb-3 flex items-center justify-between">
          <span className="font-bold text-slate-900 text-lg">
            ${Number(product.price || 0).toFixed(2)}
          </span>
        </div>

        {/* Stock and Promotions side by side */}
        <div className="flex items-start content-start gap-2 flex-wrap min-h-[64px]" onClick={(e) => e.stopPropagation()}>
          <BadgeListPopover
            items={stockItems}
            popoverTitle="Stock por Sucursal"
            icon={<PackageOpen className="h-4 w-4 text-slate-400 shrink-0" />}
          />
          {hasPromotions && (
            <div onClick={(e) => e.stopPropagation()}>
              <BadgeListPopover
                items={promoItems}
                popoverTitle="Detalles de Promoción"
                icon={<Tag className="h-4 w-4 text-purple-400 shrink-0" />}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
